import { QUESTS, VENDORS } from 'shared';
import type { QuestDefinition, ActiveQuest, VendorDefinition } from 'shared';

export interface DialogOption {
  text: string;
  icon?: string;
  color?: string;
  action: () => void;
}

export interface NPCInfo {
  id: string;
  name: string;
  type: 'quest_giver' | 'vendor' | 'trainer' | 'innkeeper';
  greeting?: string;
  portrait?: string;
}

export class DialogUI {
  private container: HTMLDivElement;
  private isOpen: boolean = false;
  private currentNPC: NPCInfo | null = null;

  // Callbacks
  public onClose?: () => void;
  public onVendorOpen?: (vendorId: string) => void;

  constructor() {
    this.container = document.createElement('div');
    this.container.id = 'dialog-frame';
    this.container.style.cssText = `
      position: absolute;
      top: 50%;
      right: 50px;
      transform: translateY(-50%);
      width: 340px;
      background: linear-gradient(180deg,
        rgba(20, 15, 10, 0.98) 0%,
        rgba(35, 25, 15, 0.98) 50%,
        rgba(20, 15, 10, 0.98) 100%);
      border: 3px solid #8b6914;
      border-radius: 8px;
      display: none;
      z-index: 300;
      pointer-events: auto;
      box-shadow:
        0 0 20px rgba(0,0,0,0.8),
        inset 0 0 60px rgba(139, 105, 20, 0.1);
      font-family: 'Georgia', serif;
    `;
    document.getElementById('game-container')?.appendChild(this.container);

    // Close on ESC
    window.addEventListener('keydown', (e) => {
      if (e.code === 'Escape' && this.isOpen) {
        this.close();
      }
    });

    // Add CSS styles for WoW-like appearance
    this.addStyles();
  }

  private addStyles(): void {
    const style = document.createElement('style');
    style.textContent = `
      #dialog-frame .dialog-header {
        background: linear-gradient(180deg, rgba(60, 45, 25, 0.9) 0%, rgba(40, 30, 15, 0.9) 100%);
        border-bottom: 2px solid #8b6914;
        padding: 12px 15px;
        display: flex;
        align-items: center;
        gap: 12px;
      }

      #dialog-frame .npc-portrait {
        width: 50px;
        height: 50px;
        border: 2px solid #8b6914;
        border-radius: 4px;
        background: linear-gradient(135deg, #2a2015 0%, #1a1510 100%);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 28px;
      }

      #dialog-frame .npc-info {
        flex: 1;
      }

      #dialog-frame .npc-name {
        color: #ffd100;
        font-size: 16px;
        font-weight: bold;
        text-shadow: 1px 1px 2px #000;
        margin: 0;
      }

      #dialog-frame .npc-title {
        color: #888;
        font-size: 11px;
        margin: 2px 0 0 0;
      }

      #dialog-frame .close-btn {
        width: 24px;
        height: 24px;
        background: linear-gradient(180deg, #4a3520 0%, #2a1a10 100%);
        border: 1px solid #8b6914;
        border-radius: 3px;
        color: #c4a000;
        font-size: 14px;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.2s;
      }

      #dialog-frame .close-btn:hover {
        background: linear-gradient(180deg, #6a4530 0%, #4a2a15 100%);
        color: #ffd100;
      }

      #dialog-frame .dialog-body {
        padding: 15px;
      }

      #dialog-frame .greeting-text {
        color: #e8d4a0;
        font-size: 13px;
        line-height: 1.5;
        margin-bottom: 15px;
        padding: 10px;
        background: rgba(0,0,0,0.3);
        border-radius: 4px;
        border-left: 3px solid #8b6914;
        font-style: italic;
      }

      #dialog-frame .dialog-options {
        display: flex;
        flex-direction: column;
        gap: 6px;
      }

      #dialog-frame .dialog-option {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 10px 12px;
        background: rgba(40, 30, 15, 0.6);
        border: 1px solid #5a4a2a;
        border-radius: 4px;
        cursor: pointer;
        transition: all 0.15s;
      }

      #dialog-frame .dialog-option:hover {
        background: rgba(80, 60, 30, 0.8);
        border-color: #8b6914;
        transform: translateX(3px);
      }

      #dialog-frame .dialog-option .option-icon {
        font-size: 18px;
        width: 24px;
        text-align: center;
      }

      #dialog-frame .dialog-option .option-text {
        flex: 1;
        color: #e8d4a0;
        font-size: 13px;
      }

      #dialog-frame .dialog-option.quest-available .option-text {
        color: #ffff00;
      }

      #dialog-frame .dialog-option.quest-complete .option-text {
        color: #ffd700;
      }

      #dialog-frame .dialog-option.vendor .option-text {
        color: #88ccff;
      }

      #dialog-frame .dialog-option .option-arrow {
        color: #666;
        font-size: 12px;
      }

      #dialog-frame .quest-detail {
        padding: 15px;
      }

      #dialog-frame .quest-title {
        color: #ffd100;
        font-size: 16px;
        font-weight: bold;
        margin: 0 0 10px 0;
        text-shadow: 1px 1px 2px #000;
      }

      #dialog-frame .quest-description {
        color: #c8b080;
        font-size: 12px;
        line-height: 1.5;
        margin-bottom: 12px;
      }

      #dialog-frame .quest-objectives {
        background: rgba(0,0,0,0.3);
        padding: 10px;
        border-radius: 4px;
        margin-bottom: 12px;
      }

      #dialog-frame .quest-objectives-title {
        color: #ffd100;
        font-size: 12px;
        margin: 0 0 6px 0;
      }

      #dialog-frame .quest-objective {
        color: #aaa;
        font-size: 11px;
        margin: 4px 0 4px 10px;
      }

      #dialog-frame .quest-objective::before {
        content: "• ";
        color: #666;
      }

      #dialog-frame .quest-rewards {
        background: rgba(50, 40, 20, 0.5);
        padding: 10px;
        border-radius: 4px;
        border: 1px solid #5a4a2a;
      }

      #dialog-frame .quest-rewards-title {
        color: #ffd100;
        font-size: 12px;
        margin: 0 0 8px 0;
      }

      #dialog-frame .reward-item {
        display: flex;
        align-items: center;
        gap: 8px;
        color: #c8b080;
        font-size: 11px;
        margin: 4px 0;
      }

      #dialog-frame .reward-xp {
        color: #aa88ff;
      }

      #dialog-frame .reward-gold {
        color: #ffd700;
      }

      #dialog-frame .dialog-buttons {
        display: flex;
        gap: 10px;
        margin-top: 15px;
        justify-content: flex-end;
      }

      #dialog-frame .dialog-btn {
        padding: 8px 20px;
        background: linear-gradient(180deg, #4a3a20 0%, #2a2010 100%);
        border: 1px solid #8b6914;
        border-radius: 4px;
        color: #ffd100;
        font-size: 12px;
        font-family: 'Georgia', serif;
        cursor: pointer;
        transition: all 0.2s;
      }

      #dialog-frame .dialog-btn:hover {
        background: linear-gradient(180deg, #6a5030 0%, #4a3020 100%);
        box-shadow: 0 0 10px rgba(255, 209, 0, 0.3);
      }

      #dialog-frame .dialog-btn.accept {
        background: linear-gradient(180deg, #2a4a20 0%, #1a3010 100%);
        border-color: #4a8b14;
      }

      #dialog-frame .dialog-btn.accept:hover {
        background: linear-gradient(180deg, #3a6a30 0%, #2a4020 100%);
      }

      #dialog-frame .dialog-btn.decline {
        background: linear-gradient(180deg, #4a2020 0%, #2a1010 100%);
        border-color: #8b1414;
      }

      #dialog-frame .separator {
        height: 1px;
        background: linear-gradient(90deg, transparent 0%, #5a4a2a 50%, transparent 100%);
        margin: 10px 0;
      }
    `;
    document.head.appendChild(style);
  }

  private getTypeTitle(type: string): string {
    switch (type) {
      case 'quest_giver': return 'Quest Giver';
      case 'vendor': return 'Merchant';
      case 'trainer': return 'Trainer';
      case 'innkeeper': return 'Innkeeper';
      default: return '';
    }
  }

  private getPortraitEmoji(type: string, name: string): string {
    // Map NPCs to emojis based on type or name
    if (name.toLowerCase().includes('elder')) return '👴';
    if (name.toLowerCase().includes('merchant') || name.toLowerCase().includes('marcus')) return '🧔';
    if (name.toLowerCase().includes('blacksmith')) return '⚒️';
    if (name.toLowerCase().includes('innkeeper') || name.toLowerCase().includes('helena')) return '👩';
    if (type === 'vendor') return '🛒';
    if (type === 'trainer') return '📚';
    return '👤';
  }

  public showNPCDialog(
    npcName: string,
    availableQuests: QuestDefinition[],
    turnInQuests: ActiveQuest[],
    onAcceptQuest: (questId: string) => void,
    onTurnInQuest: (questId: string) => void,
    npcInfo?: NPCInfo
  ): void {
    this.isOpen = true;
    this.currentNPC = npcInfo || { id: '', name: npcName, type: 'quest_giver' };
    this.container.style.display = 'block';

    const portrait = npcInfo?.portrait || this.getPortraitEmoji(npcInfo?.type || 'quest_giver', npcName);
    const title = this.getTypeTitle(npcInfo?.type || 'quest_giver');
    const greeting = npcInfo?.greeting || this.getDefaultGreeting(npcInfo?.type || 'quest_giver', npcName);

    this.container.innerHTML = `
      <div class="dialog-header">
        <div class="npc-portrait">${portrait}</div>
        <div class="npc-info">
          <div class="npc-name">${npcName}</div>
          ${title ? `<div class="npc-title">&lt;${title}&gt;</div>` : ''}
        </div>
        <button class="close-btn" id="dialog-close">✕</button>
      </div>
      <div class="dialog-body">
        <div class="greeting-text">${greeting}</div>
        <div class="dialog-options" id="dialog-options"></div>
      </div>
    `;

    const optionsContainer = document.getElementById('dialog-options')!;

    // Turn-in quests first (gold ?)
    turnInQuests.forEach((quest) => {
      const questDef = QUESTS[quest.questId];
      if (!questDef) return;

      const option = document.createElement('div');
      option.className = 'dialog-option quest-complete';
      option.innerHTML = `
        <span class="option-icon" style="color: #ffd700;">?</span>
        <span class="option-text">${questDef.name}</span>
        <span class="option-arrow">▶</span>
      `;
      option.addEventListener('click', () => {
        this.showQuestDetail(questDef, quest, onTurnInQuest, true);
      });
      optionsContainer.appendChild(option);
    });

    // Available quests (yellow !)
    availableQuests.forEach((quest) => {
      const option = document.createElement('div');
      option.className = 'dialog-option quest-available';
      option.innerHTML = `
        <span class="option-icon" style="color: #ffff00;">!</span>
        <span class="option-text">${quest.name}</span>
        <span class="option-arrow">▶</span>
      `;
      option.addEventListener('click', () => {
        this.showQuestDetail(quest, null, onAcceptQuest, false);
      });
      optionsContainer.appendChild(option);
    });

    // Vendor option if applicable
    if (npcInfo?.type === 'vendor') {
      const vendorDef = VENDORS[npcInfo.id];
      if (vendorDef) {
        if (optionsContainer.children.length > 0) {
          const separator = document.createElement('div');
          separator.className = 'separator';
          optionsContainer.appendChild(separator);
        }

        const option = document.createElement('div');
        option.className = 'dialog-option vendor';
        option.innerHTML = `
          <span class="option-icon">🛒</span>
          <span class="option-text">I want to browse your goods.</span>
          <span class="option-arrow">▶</span>
        `;
        option.addEventListener('click', () => {
          this.onVendorOpen?.(npcInfo.id);
          this.close();
        });
        optionsContainer.appendChild(option);
      }
    }

    // Innkeeper options
    if (npcInfo?.type === 'innkeeper') {
      if (optionsContainer.children.length > 0) {
        const separator = document.createElement('div');
        separator.className = 'separator';
        optionsContainer.appendChild(separator);
      }

      const restOption = document.createElement('div');
      restOption.className = 'dialog-option';
      restOption.innerHTML = `
        <span class="option-icon">🛏️</span>
        <span class="option-text">Make this inn my home.</span>
        <span class="option-arrow">▶</span>
      `;
      restOption.addEventListener('click', () => {
        // Set hearthstone location
        this.close();
      });
      optionsContainer.appendChild(restOption);
    }

    // Goodbye option
    const goodbyeOption = document.createElement('div');
    goodbyeOption.className = 'dialog-option';
    goodbyeOption.innerHTML = `
      <span class="option-icon">👋</span>
      <span class="option-text">Goodbye.</span>
    `;
    goodbyeOption.addEventListener('click', () => this.close());
    optionsContainer.appendChild(goodbyeOption);

    // Bind close button
    document.getElementById('dialog-close')?.addEventListener('click', () => this.close());
  }

  private getDefaultGreeting(type: string, name: string): string {
    switch (type) {
      case 'vendor':
        return `Welcome, traveler! Take a look at my wares. I'm sure you'll find something useful.`;
      case 'innkeeper':
        return `Welcome to my establishment! Can I interest you in a warm meal or a place to rest?`;
      case 'trainer':
        return `Greetings, adventurer. I can teach you new abilities if you're ready to learn.`;
      case 'quest_giver':
      default:
        return `Ah, an adventurer! I may have work for someone of your talents. Are you interested?`;
    }
  }

  private showQuestDetail(
    quest: QuestDefinition,
    activeQuest: ActiveQuest | null,
    onAction: (questId: string) => void,
    isTurnIn: boolean
  ): void {
    const portrait = this.currentNPC ? this.getPortraitEmoji(this.currentNPC.type, this.currentNPC.name) : '👤';
    const npcName = this.currentNPC?.name || 'NPC';
    const title = this.getTypeTitle(this.currentNPC?.type || 'quest_giver');

    this.container.innerHTML = `
      <div class="dialog-header">
        <div class="npc-portrait">${portrait}</div>
        <div class="npc-info">
          <div class="npc-name">${npcName}</div>
          ${title ? `<div class="npc-title">&lt;${title}&gt;</div>` : ''}
        </div>
        <button class="close-btn" id="dialog-close">✕</button>
      </div>
      <div class="quest-detail">
        <h3 class="quest-title">${isTurnIn ? '?' : '!'} ${quest.name}</h3>
        <p class="quest-description">${quest.description}</p>

        <div class="quest-objectives">
          <p class="quest-objectives-title">Objectives:</p>
          ${quest.objectives.map(obj => `
            <div class="quest-objective">
              ${obj.type === 'kill' ? 'Defeat' : 'Collect'} ${obj.required} ${obj.targetName}
              ${activeQuest ? ` (${this.getObjectiveProgress(activeQuest, obj.id)}/${obj.required})` : ''}
            </div>
          `).join('')}
        </div>

        <div class="quest-rewards">
          <p class="quest-rewards-title">Rewards:</p>
          <div class="reward-item reward-xp">✨ ${quest.rewards.experience} Experience</div>
          <div class="reward-item reward-gold">💰 ${quest.rewards.gold} Gold</div>
          ${quest.rewards.items ? quest.rewards.items.map(item => `
            <div class="reward-item">📦 ${item.quantity}x ${item.itemId}</div>
          `).join('') : ''}
        </div>

        <div class="dialog-buttons">
          <button class="dialog-btn" id="dialog-back">← Back</button>
          ${isTurnIn
            ? `<button class="dialog-btn accept" id="dialog-complete">Complete Quest</button>`
            : `<button class="dialog-btn accept" id="dialog-accept">Accept</button>
               <button class="dialog-btn decline" id="dialog-decline">Decline</button>`
          }
        </div>
      </div>
    `;

    // Bind events
    document.getElementById('dialog-close')?.addEventListener('click', () => this.close());
    document.getElementById('dialog-back')?.addEventListener('click', () => {
      // Go back to main dialog - need to re-fetch quest lists
      this.close();
    });

    if (isTurnIn) {
      document.getElementById('dialog-complete')?.addEventListener('click', () => {
        onAction(quest.id);
        this.close();
      });
    } else {
      document.getElementById('dialog-accept')?.addEventListener('click', () => {
        onAction(quest.id);
        this.close();
      });
      document.getElementById('dialog-decline')?.addEventListener('click', () => this.close());
    }
  }

  private getObjectiveProgress(activeQuest: ActiveQuest, objectiveId: string): number {
    const progress = activeQuest.progress.find(p => p.objectiveId === objectiveId);
    return progress?.current || 0;
  }

  public showVendorDialog(npcInfo: NPCInfo, vendorDef: VendorDefinition): void {
    this.isOpen = true;
    this.currentNPC = npcInfo;
    this.container.style.display = 'block';

    const portrait = npcInfo.portrait || this.getPortraitEmoji(npcInfo.type, npcInfo.name);

    this.container.innerHTML = `
      <div class="dialog-header">
        <div class="npc-portrait">${portrait}</div>
        <div class="npc-info">
          <div class="npc-name">${vendorDef.name}</div>
          <div class="npc-title">&lt;${vendorDef.title}&gt;</div>
        </div>
        <button class="close-btn" id="dialog-close">✕</button>
      </div>
      <div class="dialog-body">
        <div class="greeting-text">${vendorDef.greeting}</div>
        <div class="dialog-options">
          <div class="dialog-option vendor" id="browse-goods">
            <span class="option-icon">🛒</span>
            <span class="option-text">Let me browse your goods.</span>
            <span class="option-arrow">▶</span>
          </div>
          ${vendorDef.repairEnabled ? `
          <div class="dialog-option" id="repair-all">
            <span class="option-icon">🔧</span>
            <span class="option-text">Repair all my items.</span>
            <span class="option-arrow">▶</span>
          </div>
          ` : ''}
          <div class="separator"></div>
          <div class="dialog-option" id="goodbye">
            <span class="option-icon">👋</span>
            <span class="option-text">Goodbye.</span>
          </div>
        </div>
      </div>
    `;

    document.getElementById('dialog-close')?.addEventListener('click', () => this.close());
    document.getElementById('browse-goods')?.addEventListener('click', () => {
      this.onVendorOpen?.(npcInfo.id);
      this.close();
    });
    document.getElementById('repair-all')?.addEventListener('click', () => {
      // Repair logic
      this.close();
    });
    document.getElementById('goodbye')?.addEventListener('click', () => this.close());
  }

  public close(): void {
    this.isOpen = false;
    this.container.style.display = 'none';
    this.currentNPC = null;
    this.onClose?.();
  }

  public isDialogOpen(): boolean {
    return this.isOpen;
  }

  public destroy(): void {
    this.container.remove();
  }
}
